// web/src/api.js
// Central API client — all requests go through here

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

let _token     = null;
let _onLogout  = null;   // registered by App.jsx so api.js can trigger logout

// ── Token management ──────────────────────────────────────────────────────────

export const setToken       = (token) => { _token = token; };
export const getToken       = () => _token;
export const setLogoutCallback = (fn) => { _onLogout = fn; };

// ── Silent refresh ─────────────────────────────────────────────────────────────
// Deduplicates concurrent refresh attempts — if three requests 401 at once,
// they all wait on the same single refresh call rather than firing three.

let _isRefreshing   = false;
let _refreshPromise = null;

async function silentRefresh() {
  if (_isRefreshing) return _refreshPromise;

  _isRefreshing   = true;
  _refreshPromise = fetch(`${BASE_URL}/auth/refresh`, {
    method:      'POST',
    credentials: 'include',   // sends the httpOnly refreshToken cookie
  })
    .then(async (res) => {
      if (!res.ok) throw new Error('Refresh failed');
      const data = await res.json();
      setToken(data.accessToken);
      return data.accessToken;
    })
    .catch((err) => {
      // Refresh failed — clear token and kick user to login
      setToken(null);
      if (_onLogout) _onLogout();
      throw err;
    })
    .finally(() => {
      _isRefreshing   = false;
      _refreshPromise = null;
    });

  return _refreshPromise;
}

// ── Core request helper ────────────────────────────────────────────────────────
// Executes a fetch, and if the server returns 401, attempts one silent token
// refresh then retries. If the retry also 401s, triggers logout.

async function request(method, path, body) {
  const hasBody = body !== undefined && body !== null;

  const execute = async (token) => {
    return fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token   ? { Authorization: `Bearer ${token}` }  : {}),
      },
      credentials: 'include',
      body: hasBody ? JSON.stringify(body) : undefined,
    });
  };

  let res = await execute(_token);

  // Token expired — attempt one silent refresh then retry
  if (res.status === 401) {
    try {
      const newToken = await silentRefresh();
      res = await execute(newToken);
    } catch (_) {
      throw Object.assign(new Error('Session expired'), { status: 401 });
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error(err.error || 'Request failed'), { status: res.status, data: err });
  }

  return { data: await res.json() };
}

// ── Voluntary logout ──────────────────────────────────────────────────────────
// Tells the server to invalidate the refresh token + clears the httpOnly cookie.
// Always clears the local token even if the server call fails (offline, etc.).
// App.jsx calls this from its logout handler, then clears user state itself.
// (The _onLogout callback is reserved for the involuntary path — refresh failure.)

export async function logoutUser() {
  try {
    await fetch(`${BASE_URL}/auth/logout`, {
      method:      'POST',
      credentials: 'include',
      headers:     _token ? { Authorization: `Bearer ${_token}` } : {},
    });
  } catch (_) {
    // server unreachable — still clear local state below
  }
  setToken(null);
}

// ── Public API surface (unchanged shape — drop-in replacement) ────────────────

export const apiClient = {
  get:    (path)        => request('GET',    path),
  post:   (path, body)  => request('POST',   path, body),
  patch:  (path, body)  => request('PATCH',  path, body),
  delete: (path)        => request('DELETE', path),
};
